import { useMemo, useState } from 'react';
import { DateTimeUtils, lt } from '@mews/framework';
import { DateIntervalDto } from '@mews-commander/data-access';
import { Table, TableProvider, useTableState, TableHeader, StructuredDate, Order } from '@optimus-web/core';
import { useCalendarService } from '@mews-commander/ui';
import { useTrialBalanceDateIntervalSuspenseQuery } from '../queries';
import { TrialBalanceTableRow } from '../types';
import { useTableMapping } from '../hooks/useTableMapping';
import { TrialBalanceTableRightSide } from './TrialBalanceTableRightSide';

export const TrialBalanceTable = () => {
    const calendarService = useCalendarService();
    const [controlledDate, setControlledDate] = useState<StructuredDate | undefined>();
    const [tableState, setTableState] = useTableState<TrialBalanceTableRow>();

    const columns = useTableMapping();

    const {
        data: { interval: dateInterval },
    } = useTrialBalanceDateIntervalSuspenseQuery();

    const { minDate, maxDate } = useMemo(() => {
        const { start, end } = dateInterval || ({} as DateIntervalDto);
        const minDateToDate = start ? DateTimeUtils.dateIsoStringToStructureDate(start) : undefined;
        const maxDateToDate = end ? DateTimeUtils.dateIsoStringToStructureDate(end) : undefined;

        return {
            minDate: minDateToDate,
            maxDate: maxDateToDate,
        };
    }, [dateInterval]);

    const data = useMemo(() => {
        if (!minDate || !maxDate) {
            return [];
        }

        const acc: TrialBalanceTableRow[] = [];
        let date = { ...maxDate };
        while (calendarService.isDateGreaterOrEqual(date, minDate)) {
            acc.push({ date, dateISOString: calendarService.toISOString(date) });
            date = calendarService.addDays(date, -1);
        }
        return acc;
    }, [calendarService, minDate, maxDate]);

    const {
        sort: { date: dateSort },
        pagination: { pageIndex, pageSize },
    } = tableState;

    const filteredData = controlledDate
        ? data.filter((d) => d.dateISOString === calendarService.toISOString(controlledDate))
        : data;
    const sortedData = dateSort?.order === Order.Ascending ? filteredData.toReversed() : filteredData;
    const paginatedData = sortedData.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);

    return (
        <TableProvider columns={columns} state={tableState} onStateChange={setTableState}>
            <TableHeader
                title={lt('TrialBalance')}
                rightSide={
                    <TrialBalanceTableRightSide
                        minDate={minDate}
                        maxDate={maxDate}
                        controlledDate={controlledDate}
                        onDateChange={(date) => {
                            setControlledDate(date);
                            setTableState((oldTableState) => ({
                                ...oldTableState,
                                pagination: {
                                    ...oldTableState.pagination,
                                    pageIndex: 0,
                                },
                            }));
                        }}
                    />
                }
            />
            <Table
                uniqueRowId="dateISOString"
                amountOfRows={filteredData.length}
                data={paginatedData}
                data-test-id="trial-balance-table"
            />
        </TableProvider>
    );
};
